import React, { memo, useCallback, useState } from "react";
import { Dialog, useNotificationContext } from "cerulean";
import { entities, modules } from "byzantine";
import User from "byzantine/src/User";
import Account from "byzantine/src/Account";
import { Button, TextInput, Row, IconButton } from "@narmi/design_system";

import { CustomAlertPayload } from "byzantine/src/dbbl/businessLogic/modules/alerts/custom";
import { useLocalization } from "@fluent/react";
import { useCurrentUser } from "../../../../../contexts/CurrentUserContext";
import AccountSelector from "../AccountSelector";
import DeliveryMethod from "../delivery";
import SmsTransactionalDisclosureDialog from "../../../SmsTransactionalDisclosureDialog";

import * as formAdapters from "../../../../../../src/adapters";

interface NewAlertDialogProps {
  accounts: Account[];
  dialogIsOpen: boolean;
  onCreateAlert: (payload: CustomAlertPayload, onFinally?: () => void) => void;
  setDialogIsOpen: (isOpen: boolean) => void;
}

const SectionTitle = memo(({ sectionLabel }: { sectionLabel: string }) => (
  <header
    className={
      "margin--bottom--xs  fontWeight--semibold \
    fontFamily--body fontSize--m fontWeight--semibold fontColor--heading"
    }
  >
    {sectionLabel}
  </header>
));

const NewAlertDialog = memo(
  ({
    accounts,
    dialogIsOpen,
    onCreateAlert,
    setDialogIsOpen,
  }: NewAlertDialogProps) => {
    const { l10n } = useLocalization();
    const { currentUser, setCurrentUser } = useCurrentUser();
    const { sendNotification } = useNotificationContext();
    const { acceptTransactionalSmsDisclosure, loading: smsDisclosureLoading } =
      entities.useAcceptTransactionalSmsDisclosure();
    const [showDisclosure, setShowDisclosure] = useState(false);

    const { form, createRequestPayload, onValidate, resetForm } =
      modules.customAlerts.useCustomAlertForm();
    const { value, onChange, error, errorText } = formAdapters.select(
      form,
      "accounts"
    );

    const handleDeliveryMethodChange = useCallback(
      (channel: API.DeliveryChannel) => {
        if (channel === "sms" && !currentUser?.has_accepted_transactional_sms) {
          setShowDisclosure(true);
        } else {
          form.setFieldValue("deliveryMethod", channel);
        }
      },
      [currentUser, form]
    );

    const onSave = useCallback(async () => {
      if (await onValidate()) {
        const payload = createRequestPayload();
        onCreateAlert(payload, resetForm);
      }
    }, [onValidate, createRequestPayload, onCreateAlert, resetForm]);

    const onClose = useCallback(() => {
      resetForm();
      setDialogIsOpen(false);
    }, [resetForm, setDialogIsOpen]);

    return (
      <>
        <Dialog
          isOpen={dialogIsOpen && !showDisclosure}
          onUserDismiss={onClose}
          title={l10n.getString(
            "custom-alerts-modal-title",
            {},
            "Create a custom alert"
          )}
          footer={
            <Row justifyContent="end" alignItems="center">
              <Row.Item shrink>
                <Button
                  kind="plain"
                  label={l10n.getString(
                    "custom-alerts-button-close",
                    {},
                    "Close"
                  )}
                  type="button"
                  onClick={onClose}
                />
              </Row.Item>
              <Row.Item shrink>
                <Button
                  kind="primary"
                  label={l10n.getString("custom-alerts-button-add", {}, "Add")}
                  type="button"
                  onClick={onSave}
                />
              </Row.Item>
            </Row>
          }
        >
          <p className="margin--y--m fontSize--s">
            {l10n.getString(
              "custom-alerts-description",
              {},
              "Alert me when a transaction:"
            )}
          </p>
          <SectionTitle
            sectionLabel={l10n.getString(
              "custom-alerts-form-title-delivery-method",
              {},
              "Delivery method (select one)"
            )}
          />
          <div className="margin--bottom--m">
            <DeliveryMethod
              {...formAdapters.select(form, "deliveryMethod")}
              onChange={handleDeliveryMethodChange}
            />
          </div>

          <SectionTitle
            sectionLabel={l10n.getString(
              "custom-alerts-form-title-account",
              {},
              "Account"
            )}
          />
          <div className="margin--bottom--m">
            <AccountSelector
              value={value}
              error={error}
              errorText={errorText}
              onChange={onChange as any}
              accounts={accounts.filter((a) => a.isInternal())}
              selectAll={false}
            />
          </div>

          <SectionTitle
            sectionLabel={l10n.getString(
              "custom-alerts-form-title-transaction-amount",
              {},
              "Transaction amount"
            )}
          />
          <div className="margin--bottom--m">
            <TextInput
              label={l10n.getString(
                "custom-alerts-form-label-amount-greater",
                {},
                "Amount greater than"
              )}
              endContent={
                <IconButton
                  name={"x"}
                  onClick={() =>
                    form.resetFieldToInitialValue("greaterThanAmount")
                  }
                />
              }
              {...formAdapters.amountInput(form, "greaterThanAmount")}
            />
            <div className="margin--top--m">
              <TextInput
                label={l10n.getString(
                  "custom-alerts-form-label-amount-lesser",
                  {},
                  "Amount less than"
                )}
                endContent={
                  <IconButton
                    name={"x"}
                    onClick={() =>
                      form.resetFieldToInitialValue("lessThanAmount")
                    }
                  />
                }
                {...formAdapters.amountInput(form, "lessThanAmount")}
              />
            </div>
          </div>

          <SectionTitle
            sectionLabel={l10n.getString(
              "custom-alerts-form-title-resulting-balance",
              {},
              "Resulting balance"
            )}
          />
          <div className="margin--bottom--m">
            <TextInput
              label={l10n.getString(
                "custom-alerts-form-label-balance-greater",
                {},
                "Balance greater than"
              )}
              endContent={
                <IconButton
                  name={"x"}
                  onClick={() =>
                    form.resetFieldToInitialValue("greaterThanBalance")
                  }
                />
              }
              {...formAdapters.amountInput(form, "greaterThanBalance")}
            />
            <div className="margin--top--m">
              <TextInput
                label={l10n.getString(
                  "custom-alerts-form-label-balance-lesser",
                  {},
                  "Balance less than"
                )}
                endContent={
                  <IconButton
                    name={"x"}
                    onClick={() =>
                      form.resetFieldToInitialValue("lessThanBalance")
                    }
                  />
                }
                {...formAdapters.amountInput(form, "lessThanBalance")}
              />
            </div>
          </div>

          <SectionTitle
            sectionLabel={l10n.getString(
              "custom-alerts-form-title-description",
              {},
              "Contains the following keywords"
            )}
          />
          <div className="margin--bottom--m">
            <TextInput
              field="keywords"
              label={l10n.getString(
                "custom-alerts-form-placeholder-description",
                {},
                "e.g. Venmo"
              )}
              {...formAdapters.input(form, "description")}
              multiline
            />
            <p className="margin--top--xxs fontSize--s">
              {l10n.getString(
                "custom-alerts-form-subtext-description",
                {},
                "Use commas to separate keywords"
              )}
            </p>
          </div>
        </Dialog>
        <SmsTransactionalDisclosureDialog
          isOpen={showDisclosure}
          onClose={() => setShowDisclosure(false)}
          onAccept={() =>
            acceptTransactionalSmsDisclosure({
              onSuccess: () => {
                if (currentUser) {
                  setCurrentUser(
                    new User({
                      ...currentUser,
                      has_accepted_transactional_sms: true,
                    })
                  );
                }
                form.setFieldValue("deliveryMethod", "sms");
                setShowDisclosure(false);
              },
              onError: () => {
                sendNotification({
                  type: "negative",
                  text: l10n.getString("error-unknown-try-again"),
                });
                setShowDisclosure(false);
              },
            })
          }
          loading={smsDisclosureLoading}
        />
      </>
    );
  }
);

export default NewAlertDialog;
